import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { FeeCategory, GroupedPaymentMethod, PaymentSummaryResponse } from '../utils/types';
import { getProcessingFeeDetails } from '../../auth/_requests';
import { useAuth } from '../../auth/Auth';

interface PaymentContextProps {
  paymentSummary: PaymentSummaryResponse | undefined;
  setPaymentSummary: (summary: PaymentSummaryResponse) => void;
  groupedFee: GroupedPaymentMethod[];
}

const PaymentContext = createContext<PaymentContextProps | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummaryResponse | undefined>();
  const { auth } = useAuth();

  const token = auth?.data.token || '';

  const [groupedFee, setGroupedFee] = useState<GroupedPaymentMethod[]>([]);

  useEffect(() => {
    if (!token) return;

    const fetchFees = async () => {
      try {
        const response = await getProcessingFeeDetails(token);
        const fees = (response?.data || []).filter(
          (fee) => fee.paymentMethodName === 'Credit Card' && fee.cardCompanyTypeValue !== 'Amex'
        );

        const grouped: Record<
          string,
          Record<
            string,
            { directRate?: string; subscriptionRate?: string; categories: FeeCategory[] }
          >
        > = {};
        // grouped[company][category] = { directRate, subscriptionRate, categories: [] }

        fees.forEach((fee) => {
          const company = fee.cardCompanyTypeValue!;
          const categoryName = fee.cardCategoryName ?? 'General';
          const categoryId = fee.tenantProcessingFeeId;

          if (!grouped[company]) grouped[company] = {};
          if (!grouped[company][categoryName]) {
            grouped[company][categoryName] = {
              categories: [],
              directRate: undefined,
              subscriptionRate: undefined,
            };
          }

          const catGroup = grouped[company][categoryName];
          if (fee.paymentType === 'direct') catGroup.directRate = `${fee.percentageRate}%`;
          if (fee.paymentType === 'subscription')
            catGroup.subscriptionRate = `${fee.percentageRate}%`;
          catGroup.categories.push({ id: categoryId, name: categoryName });
        });

        // Transform to GroupedPaymentMethod[]
        const compactData: GroupedPaymentMethod[] = [
          {
            method: 'Credit Card',
            companies: Object.entries(grouped).map(([company, cats]) => ({
              name: company,
              categories: Object.values(cats).map((c) => ({
                id: c.categories[0].id, // pick first ID
                name: c.categories[0].name,
                directRate: c.directRate,
                subscriptionRate: c.subscriptionRate,
              })),
            })),
          },
        ];

        setGroupedFee(compactData);
      } catch (error) {
        console.error(error);
      }
    };

    fetchFees();
  }, [token]);

  return (
    <PaymentContext.Provider value={{ paymentSummary, setPaymentSummary, groupedFee }}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
